import { Injectable } from '@angular/core';
import { QueryPartStrategy } from '../../../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../../../advanced-search/filters/base/entities/query-part.class';
import { Combine, Restriction } from '../../../api/entities/api-query.entity';
import { Group } from '../../../groups/entities/group.entity';
import { RecordTypesService } from '../../../record-types/services/record-types.service';
import { Zone } from '../../../zones/entities/zone.entity';
import { MediaType, RecordStatus, TernaryLogic } from '../../entities/metadata/metadata.entity';
import { RecordRestrictions } from './record-restrictions.helper';

@Injectable({ providedIn: 'root' })
export class ZoneRestrictions {
    constructor(private readonly recordTypesService: RecordTypesService) {}

    public static fullZoneSearch(): Restriction {
        return new QueryPart({
            // Counteract the default filter on IsInIngestSpace:false
            field: 'IsInIngestSpace',
            strategy: QueryPartStrategy.MatchAll,
            values: [TernaryLogic.both]
        });
    }

    public static allRecordsInZone(): Restriction {
        return Combine.And(
            ZoneRestrictions.onlyRecordsInZone(),
            RecordRestrictions.includeLogicallyDeletedRecordsRestriction()
        );
    }

    private static onlyRecordsInZone(): Restriction {
        return new QueryPart({
            // Counteract the default filter on IsInIngestSpace:false
            field: 'IsInIngestSpace',
            strategy: QueryPartStrategy.MatchAll,
            values: [TernaryLogic.true]
        });
    }

    public regularRecordTypesZone(): Restriction {
        return this.allNonDeletedRecordsZone();
    }

    public allNonDeletedRecordsZone(): Restriction {
        return Combine.And(
            RecordRestrictions.includeRecordTypesRestriction(this.recordTypesService.getOverviewRecordTypes()),
            ZoneRestrictions.fullZoneSearch(),
            RecordRestrictions.nonDeletedZoneRestriction()
        );
    }

    // Recycled restriction now handled by /recycled endpoint
    public deletedRecordTypesZone(zones: Zone[]): Restriction {
        return Combine.And(
            RecordRestrictions.includeRecordTypesRestriction(this.recordTypesService.getOverviewRecordTypes()),
            RecordRestrictions.onlyDeletedRecordsRestriction(zones),
            RecordRestrictions.excludeMediaTypes([MediaType.audiofragment, MediaType.videofragment]),
            RecordRestrictions.excludeRecordStatusesRestriction([RecordStatus.Rejected]),
            ZoneRestrictions.fullZoneSearch()
        );
    }

    public allParentRecordsInZone(zones: Zone[], groups: Group[]): Restriction {
        return Combine.And(
            ZoneRestrictions.fullZoneSearch(),
            Combine.Or(
                // MED-5852: Should be able to select records in any phase
                Combine.And(
                    RecordRestrictions.excludeConceptPhaseRestriction(),
                    RecordRestrictions.editableRestriction(groups)
                ),
                Combine.And(
                    RecordRestrictions.conceptZoneRestriction(zones),
                    RecordRestrictions.excludeRecordStatusesRestriction([RecordStatus.Deleted])
                )
            )
        );
    }
}
